import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { getMomentLocale } from '@helpers/locale';
import CardActions from '@components/asset/modal/tabs/workflow/attachment_details/versions_sidebar/card_actions';

const EventCard = (props) => {
  const {
    annotationMode,
    annotationNumber,
    cardEvent,
    childEvent,
    commentsFeatureEnabled,
    isActiveVersion,
    onDelete,
    onReply,
    parentId,
    setTempAnnotation,
    users,
  } = props;

  const {
    author,
    children,
    created_at,
    id,
    note,
    status,
    type,
    updated_at,
    x_offset_in_percentage,
    y_offset_in_percentage,
  } = cardEvent;

  if (type === 'annotation' && !annotationMode) { return null; }
  if (type === 'comment' && commentsFeatureEnabled === false) { return null; }

  const renderHeader = () => {
    const firstName = users[author] ? users[author].first_name : '';
    const lastName = users[author] ? users[author].last_name : '';

    switch (type) {
      case 'annotation':
        return (
          <>
            {!childEvent && (
              <div className="annotation-number">
                <span>{annotationNumber}</span>
              </div>
            )}
            <div className="name-container">
              <p className="name">{firstName} {lastName}</p>
              <p className="type-of-action">{t('wrote an annotation:')}</p>
            </div>
          </>
        );
      case 'comment':
        return (
          <>
            {!childEvent && <span className="bff-comment icon" />}
            <div className="name-container">
              <p className="name">{firstName} {lastName}</p>
              <p className="type-of-action">{childEvent ? t('replied:') : t('left a comment:')}</p>
            </div>
          </>
        );
      case 'reservation':
        return (
          <>
            {!childEvent && (
              <span className={`icon bff-${status === 'checked_out' ? 'check-out' : 'check-in'}`} />
            )}
            <div className="name-container">
              <p className="name">{firstName} {lastName}</p>
              <p className="type-of-action">
                {`checked ${status === 'checked_out' ? 'out' : 'in'} the file`}
              </p>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderBody = () => {
    switch (type) {
      case 'annotation':
      case 'comment':
      case 'reservation':
        return Boolean(note) && <p>{note}</p>;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (type) {
      case 'annotation':
      case 'comment':
        return (
          <CardActions
            cardEvent={cardEvent}
            onDelete={onDelete}
            onReply={onReply}
            parentId={parentId || id}
          />
        );
      case 'reservation': {
        const timestamp = moment(updated_at || created_at).locale(getMomentLocale()).format('LL');
        return <p className="event-timestamp">{timestamp}</p>;
      }
      default:
        return null;
    }
  };

  const renderChildren = () => {
    if (!Array.isArray(children)) { return null; }

    return (
      <ul>
        {children.map((childrenEvent) => (
          <EventCard
            key={childrenEvent.id}
            cardEvent={childrenEvent}
            childEvent="child-event"
            onReply={onReply}
            onDelete={onDelete}
            parentId={id}
            users={users}
          />
        ))}
      </ul>
    );
  };

  const selectAnnotation = () => {
    setTempAnnotation({
      x: x_offset_in_percentage,
      y: y_offset_in_percentage,
      comment: note,
      number: annotationNumber
    });
  };

  return (
    <li
      className={`event ${childEvent} ${type}-event ${isActiveVersion ? 'active' : ''}`}
      onClick={(type === 'annotation' && isActiveVersion) ? () => selectAnnotation() : undefined}
    >
      <div className="event__header">
        {renderHeader()}
      </div>
      <div className="event__body">
        {renderBody()}
      </div>
      <div className="event__footer">
        {renderFooter()}
      </div>
      {renderChildren()}
    </li>
  );
};

EventCard.propTypes = {
  annotationMode: PropTypes.bool,
  annotationNumber: PropTypes.number,
  cardEvent: PropTypes.shape({
    author: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
    created_at: PropTypes.string,
    id: PropTypes.string,
    note: PropTypes.string,
    status: PropTypes.string,
    type: PropTypes.oneOf(['annotation', 'comment', 'reservation']),
    updated_at: PropTypes.string,
    x_offset_in_percentage: PropTypes.string,
    y_offset_in_percentage: PropTypes.string,
  }).isRequired,
  childEvent: PropTypes.string,
  commentsFeatureEnabled: PropTypes.bool,
  isActiveVersion: PropTypes.bool,
  onDelete: PropTypes.func,
  onReply: PropTypes.func,
  parentId: PropTypes.string,
  setTempAnnotation: PropTypes.func,
  users: PropTypes.shape({}).isRequired,
};

EventCard.defaultProps = {
  annotationMode: undefined,
  annotationNumber: null,
  childEvent: '',
  commentsFeatureEnabled: undefined,
  isActiveVersion: undefined,
  onDelete: undefined,
  onReply: undefined,
  setTempAnnotation: undefined,
  parentId: null,
};

export default EventCard;
